import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_BASE_URL } from '@config/api';
import { api } from '@services/authService';
import { useSafeNavigate } from '../hooks/useSafeNavigate';
import { normalizeUserFacingErrorMessage } from '@utils/errorMessage';

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  name?: string; // computed name field
  role?: string;
  status?: string;
  clinicId?: string; // added to access the clinic for clinic_admin users
  clinicName?: string; // name of the clinic for display
  shelterId?: string; // added to access the shelter for shelter users
  shelterName?: string; // name of the shelter for display
  createdAt?: string; // date when user was created
  planFeatures?: string[]; // features from subscription plan
  activePlan?: {
    id: string;
    name: string;
    features: string[];
    maxUsers: number;
    maxAnimals: number;
    maxAppointments: number;
    endDate: string;
  };
  clinic?: {
    id: string;
    name: string;
    cui: string;
    email: string;
    phone: string;
    address: {
      street: string;
      streetNumber: string;
      city: string;
      county: string;
      postalCode: string;
      country?: string;
    };
    logo?: string;
    status: string;
  };
}

interface LoginResponse {
  success: boolean;
  token?: string;
  user?: User;
  message?: string;
  needsActivation?: boolean;
  error?: string;
  email?: string;
  hasTemporaryPassword?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  authReady: boolean;
  isImpersonating: boolean;
  originalUser: User | null;
  hasTemporaryPassword: boolean;
  login: (email: string, password: string) => Promise<LoginResponse>;
  logout: () => void;
  signOut: () => Promise<void>;
  toggleTheme: () => void;
  endImpersonation: () => Promise<void>;
  startImpersonation: (targetUserId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  isAuthenticated: false,
  authReady: false,
  isImpersonating: false,
  originalUser: null,
  hasTemporaryPassword: false,
  login: async () => ({ success: false }),
  logout: () => {},
  signOut: async () => {},
  toggleTheme: () => {},
  endImpersonation: async () => {},
  startImpersonation: async () => {},
});

export const useAuth = () => useContext(AuthContext);

const normalizeUserRole = (role?: string): string | undefined => {
  if (role === 'receptionist') {
    return 'reception';
  }

  return role;
};

const normalizeUserData = <T extends User | null>(rawUser: T): T => {
  if (!rawUser) {
    return rawUser;
  }

  const normalized = {
    ...rawUser,
    role: normalizeUserRole(rawUser.role),
  };

  if (!normalized.name && (normalized.firstName || normalized.lastName)) {
    normalized.name = `${normalized.firstName || ''} ${normalized.lastName || ''}`.trim();
  }

  return normalized as T;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [originalUser, setOriginalUser] = useState<User | null>(null);
  const [hasTemporaryPassword, setHasTemporaryPassword] = useState(false);
  
  const navigate = useSafeNavigate();

  const isAuthenticated = !!token;

  const login = async (email: string, password: string): Promise<LoginResponse> => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const text = await response.text();
      let data: Record<string, unknown> = {};
      try {
        data = text ? (JSON.parse(text) as Record<string, unknown>) : {};
      } catch {
        // Serverul a returnat HTML (ex. 404) în loc de JSON - backend probabil nepor-nit sau ruta greșită
        if (response.status === 404 || text.trimStart().startsWith('<!')) {
          return {
            success: false,
            message:
              'Nu s-a găsit API-ul. Pornește backend-ul pe http://localhost:3000 (ruta /api/v1); frontend-ul rulează separat pe portul 3005.',
          };
        }
        return { success: false, message: 'Răspuns invalid de la server.' };
      }

      const okBody = typeof data.success === 'boolean' ? data.success : false;
      if (response.ok && okBody) {
        const payload = data.data as Record<string, unknown>;
        const token = payload.token as string;
        const refreshToken = payload.refreshToken as string | undefined;
        
        // Seteaza token-ul pentru a putea face request-uri autentificate
        setToken(token);
        localStorage.setItem('token', token);
        if (refreshToken) {
          localStorage.setItem('refreshToken', refreshToken);
        }
        localStorage.setItem('hasTemporaryPassword', String(payload.hasTemporaryPassword || false));
        setHasTemporaryPassword(Boolean(payload.hasTemporaryPassword));

        // Incarca datele complete ale utilizatorului din endpoint-ul /me
        try {
          const meResponse = await fetch(`${API_BASE_URL}/auth/me`, {
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
          });

          if (meResponse.ok) {
            const meData = await meResponse.json();
            if (meData.success) {
              const normalizedUser = normalizeUserData(meData.data);
              setUser(normalizedUser);
              localStorage.setItem('user', JSON.stringify(normalizedUser));
              
              return { 
                success: true, 
                token: token, 
                user: normalizedUser,
                hasTemporaryPassword: Boolean(payload.hasTemporaryPassword)
              };
            }
          }
        } catch (meError) {
          console.warn('Nu s-au putut incarca datele complete ale utilizatorului:', meError);
        }

        // Fallback la datele de baza daca /me nu functioneaza
        const fallbackUser = normalizeUserData(payload.user as User);
        setUser(fallbackUser);
        localStorage.setItem('user', JSON.stringify(fallbackUser));

        return { 
          success: true, 
          token: token, 
          user: fallbackUser,
          hasTemporaryPassword: Boolean(payload.hasTemporaryPassword)
        };
      }

      const failureText = normalizeUserFacingErrorMessage(
        data.error !== undefined ? data.error : data.message !== undefined ? data.message : 'Eroare la autentificare'
      );

      // Tratam cont neactivat (mesaj de la backend poate mentiona activarea)
      if (response.status === 403 && failureText.toLowerCase().includes('activat')) {
        return {
          success: false,
          needsActivation: true,
          message: failureText,
          email,
        };
      }

      return { success: false, message: failureText };
    } catch (error: unknown) {
      return {
        success: false,
        message: normalizeUserFacingErrorMessage(error),
      };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setIsImpersonating(false);
    setOriginalUser(null);
    setHasTemporaryPassword(false);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('originalUser');
    localStorage.removeItem('isImpersonating');
    localStorage.removeItem('hasTemporaryPassword');
  };

  const signOut = async () => {
    try {
      // Invalidate token on server
      if (token) {
        await api.post('/auth/logout', {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      // Continue with logout even if server call fails
      console.warn('Logout server call failed:', error);
    } finally {
      // Clear all client-side data
      logout();
      
      // Clear any cached data
      if (typeof window !== 'undefined') {
        // Clear localStorage
        localStorage.clear();
        
        // Clear sessionStorage
        sessionStorage.clear();
        
        // Clear any other cached data
        if ('caches' in window) {
          caches.keys().then(names => {
            names.forEach(name => caches.delete(name));
          });
        }
      }
      
      // Redirect to login
      navigate('/login', { replace: true });
    }
  };

  const toggleTheme = () => {
    const currentTheme = localStorage.getItem('theme-mode') || 'light';
    const newTheme = currentTheme === 'light' ? 'dark' : 'light';
    localStorage.setItem('theme-mode', newTheme);
    
    // Dispatch theme change event
    window.dispatchEvent(new CustomEvent('theme-change', { detail: newTheme }));
    
    // Force page reload to apply theme changes
    window.location.reload();
  };

  const startImpersonation = async (targetUserId: string) => {
    try {
      const response = await api.post(`/auth/impersonate/${targetUserId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        // Store original user
        setOriginalUser(user);
        setIsImpersonating(true);
        
        // Update current user to impersonated user
        const impersonatedUser = normalizeUserData(response.data.user);
        setUser(impersonatedUser);
        setToken(response.data.token);
        
        // Store in localStorage
        localStorage.setItem('originalUser', JSON.stringify(user));
        localStorage.setItem('isImpersonating', 'true');
        localStorage.setItem('user', JSON.stringify(impersonatedUser));
        localStorage.setItem('token', response.data.token);
      }
    } catch (error) {
      console.error('Impersonation failed:', error);
      throw error;
    }
  };

  const endImpersonation = async () => {
    try {
      if (originalUser && token) {
        await api.post('/auth/end-impersonation', {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.warn('End impersonation server call failed:', error);
    } finally {
      // Restore original user
      if (originalUser) {
        setUser(originalUser);
        setOriginalUser(null);
        setIsImpersonating(false);
        
        // Clear impersonation data from localStorage
        localStorage.removeItem('originalUser');
        localStorage.removeItem('isImpersonating');
      }
    }
  };

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const storedOriginalUser = localStorage.getItem('originalUser');
    const storedIsImpersonating = localStorage.getItem('isImpersonating');
    const storedHasTemporaryPassword = localStorage.getItem('hasTemporaryPassword');
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      const userData = normalizeUserData(JSON.parse(storedUser));
      
      setUser(userData);
      
      // Incarca datele complete din /me pentru a obtine informatiile despre clinica
      const loadCompleteUserData = async () => {
        try {
          const meResponse = await fetch(`${API_BASE_URL}/auth/me`, {
            headers: { 
              'Authorization': `Bearer ${storedToken}`,
              'Content-Type': 'application/json'
            },
          });

          if (meResponse.ok) {
            const meData = await meResponse.json();
            if (meData.success) {
              const normalizedUser = normalizeUserData(meData.data);
              setUser(normalizedUser);
              localStorage.setItem('user', JSON.stringify(normalizedUser));
            }
          }
        } catch (error: any) {
          // Nu logăm eroarea dacă backend-ul nu rulează
          if (!(error?.message?.includes('Failed to fetch') || error?.message?.includes('Network Error') || error?.name === 'TypeError')) {
            console.warn('Nu s-au putut incarca datele complete ale utilizatorului:', error);
          }
        }
      };
      
      loadCompleteUserData();
    }
    
    if (storedHasTemporaryPassword) {
      setHasTemporaryPassword(storedHasTemporaryPassword === 'true');
    }
    
    if (storedOriginalUser) {
      setOriginalUser(normalizeUserData(JSON.parse(storedOriginalUser)));
    }
    
    if (storedIsImpersonating === 'true') {
      setIsImpersonating(true);
    }
    
    setAuthReady(true);
  }, []);

  // Listen for force logout events
  useEffect(() => {
    const handleForceLogout = () => {
      signOut();
    };

    window.addEventListener('force-logout', handleForceLogout);
    return () => window.removeEventListener('force-logout', handleForceLogout);
  }, []);

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      isAuthenticated, 
      authReady, 
      isImpersonating,
      originalUser,
      hasTemporaryPassword,
      login, 
      logout,
      signOut,
      toggleTheme,
      endImpersonation,
      startImpersonation
    }}>
      {children}
    </AuthContext.Provider>
  );
}; 
